/**
 * Render an `AfsAnnotation` as markdown.
 *
 * Mirrors hovercontext (was: agentation.com)'s four detail levels exactly:
 *   - `compact`  — quick feedback with minimal context.
 *   - `standard` — balanced detail with location + classes (default).
 *   - `detailed` — adds bounding boxes, nearby text, full component tree.
 *   - `forensic` — everything including computed styles.
 */
const escapeBackticks = (s) => s.replace(/```/g, "``\\`");
const fmtBox = (b) => `x=${Math.round(b.x)} y=${Math.round(b.y)} ${Math.round(b.width)}×${Math.round(b.height)}`;
const headerFor = (a) => {
    const kind = a.kind ?? "feedback";
    const intent = a.intent ? ` · intent=${a.intent}` : "";
    const severity = a.severity ? ` · severity=${a.severity}` : "";
    return `# AFS \`${a.id}\` — ${kind}${intent}${severity}`;
};
const commentBlock = (a) => `> ${a.comment.replace(/\n/g, "\n> ")}`;
const targetBlock = (a, detail) => {
    const lines = [];
    lines.push(`- **Element:** \`<${a.element}>\``);
    lines.push(`- **Path:** \`${a.elementPath}\``);
    if (a.url)
        lines.push(`- **URL:** ${a.url}`);
    if (detail !== "compact" && a.cssClasses) {
        lines.push(`- **Classes:** \`${a.cssClasses}\``);
    }
    if (detail === "detailed" || detail === "forensic") {
        if (a.boundingBox)
            lines.push(`- **Box:** ${fmtBox(a.boundingBox)}`);
        lines.push(`- **Position:** x=${a.x.toFixed(1)}% y=${Math.round(a.y)}px`);
        if (a.nearbyText) {
            lines.push(`- **Nearby text:** ${JSON.stringify(a.nearbyText)}`);
        }
        if (a.selectedText) {
            lines.push(`- **Selected:** ${JSON.stringify(a.selectedText)}`);
        }
        if (a.accessibility)
            lines.push(`- **A11y:** ${a.accessibility}`);
    }
    if (detail === "forensic") {
        if (a.fullPath)
            lines.push(`- **Full path:** \`${a.fullPath}\``);
        if (a.nearbyElements) {
            lines.push(`- **Nearby DOM:** ${a.nearbyElements}`);
        }
        if (a.isFixed)
            lines.push(`- **Fixed/sticky:** yes`);
        if (a.isMultiSelect)
            lines.push(`- **Multi-select:** yes`);
    }
    return lines.join("\n");
};
const componentsBlock = (a, detail) => {
    if (detail === "compact")
        return null;
    if (!a.reactComponents)
        return null;
    return `## Components\n\`\`\`\n${escapeBackticks(a.reactComponents)}\n\`\`\``;
};
const stylesBlock = (a) => {
    if (!a.computedStyles)
        return null;
    return `## Computed styles\n\`\`\`\n${escapeBackticks(a.computedStyles)}\n\`\`\``;
};
const placementBlock = (a) => {
    if (!a.placement)
        return null;
    const p = a.placement;
    const lines = [
        "## Placement",
        `- **Component type:** \`${p.componentType}\``,
        `- **Size:** ${p.width}×${p.height}`,
        `- **Scroll Y:** ${p.scrollY}`,
    ];
    if (p.text)
        lines.push(`- **Text:** ${JSON.stringify(p.text)}`);
    return lines.join("\n");
};
const rearrangeBlock = (a) => {
    if (!a.rearrange)
        return null;
    const r = a.rearrange;
    return [
        "## Rearrange",
        `- **Target:** \`${r.selector}\` (${r.label})`,
        `- **Tag:** \`<${r.tagName}>\``,
        `- **From:** ${fmtBox(r.originalRect)}`,
        `- **To:** ${fmtBox(r.currentRect)}`,
    ].join("\n");
};
const threadBlock = (a) => {
    if (!a.thread || a.thread.length === 0)
        return null;
    const lines = ["## Thread"];
    for (const m of a.thread) {
        const who = m.by === "agent" ? `🤖 ${m.agentId ?? "agent"}` : "🧑 human";
        lines.push(`**${who}** · ${m.at}\n${m.text.replace(/^/gm, "> ")}`);
    }
    return lines.join("\n\n");
};
const resolutionBlock = (a) => {
    if (a.status === "pending")
        return null;
    const lines = [`## Status: ${a.status}`];
    if (a.resolvedAt)
        lines.push(`- **Resolved at:** ${a.resolvedAt}`);
    if (a.resolvedBy)
        lines.push(`- **Resolved by:** ${a.resolvedBy}`);
    return lines.join("\n");
};
const attachmentsBlock = (a) => {
    if (!a.attachments)
        return null;
    const lines = ["## Attachments"];
    if (a.attachments.screenshot) {
        lines.push(`![screenshot](${a.attachments.screenshot})`);
    }
    if (a.attachments.outerHTML) {
        lines.push("```html\n" + escapeBackticks(a.attachments.outerHTML) + "\n```");
    }
    return lines.length > 1 ? lines.join("\n\n") : null;
};
export const annotationToMarkdown = (annotation, detail = "standard") => {
    const sections = [
        headerFor(annotation),
        commentBlock(annotation),
        `## Target\n${targetBlock(annotation, detail)}`,
        componentsBlock(annotation, detail),
        detail === "forensic" ? stylesBlock(annotation) : null,
        placementBlock(annotation),
        rearrangeBlock(annotation),
        attachmentsBlock(annotation),
        threadBlock(annotation),
        resolutionBlock(annotation),
    ];
    return sections.filter(Boolean).join("\n\n");
};
export const annotationsToMarkdown = (annotations, detail = "standard") => annotations.length === 0
    ? "_(no annotations)_"
    : annotations.map((a) => annotationToMarkdown(a, detail)).join("\n\n---\n\n");
export const annotationToJson = (a) => JSON.stringify(a, null, 2);
//# sourceMappingURL=serialize.js.map